<template>
  <div>
    <div class="filter-box">
      <span class="subheading">{{ $t('general.query.filters') }}</span>
      <v-select
        v-model="errorCodeFilter"
        multiple
        :items="availableCodes.map(codeObject => ({
          value: codeObject.errorCode,
          text: `${codeObject.errorCode}: ${codeObject.errorDescription}`
        }))"
        :label="$t('general.admin.tabs.errors.errorCodes')"
        class="filter-input"
      />
    </div>
    <v-data-table
      :headers="headers"
      :items="errors"
      :locale="$i18n.locale"
      :no-data-text="$t('general.admin.tables.no_data_text')"
      :pagination.sync="pagination"
      :loading="$apollo.queries.errors.loading"
      :total-items="totalCount"
      class="admin-table"
    >
      <template v-slot:items="props">
        <td>{{ props.item.errorCode }}</td>
        <td>{{ props.item.errorDescription }}</td>
        <td>{{ props.item.count }}</td>
        <td>{{ formatTime(props.item.firstErrorAt) }}</td>
        <td>{{ formatTime(props.item.lastErrorAt) }}</td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
// eslint-disable-next-line
import gql from "graphql-tag";
import { DateTime } from 'luxon';

export default {
  name: 'ErrorTable',

  props: {
    deviceId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      errors: [],
      availableCodes: [],
      pagination: {},
      totalCount: 0,
      errorCodeFilter: [],
    };
  },

  created() {
    this.headers = [
      {
        text: this.$t('general.admin.tabs.devices.deviceErrors.errorCode'),
        value: 'errorCode',
      },
      {
        text: this.$t('general.admin.tabs.devices.deviceErrors.errorDescription'),
        value: 'errorDescription',
      },
      {
        text: this.$t('general.admin.tabs.devices.deviceErrors.count'),
        value: 'count',
      },
      {
        text: this.$t('general.admin.tabs.devices.deviceErrors.firstErrorAt'),
        value: 'firstErrorAt',
      },
      {
        text: this.$t('general.admin.tabs.devices.deviceErrors.lastErrorAt'),
        value: 'lastErrorAt',
      },
    ];
  },

  apollo: {
    availableCodes: {
      query: gql`
        query GetErrorCodes(
          $deviceId: uuid!
        ) {
          device_errors (
            where: {
              deviceId: {
                _eq: $deviceId
              }
            }
            distinct_on: errorCode
          ) {
            errorCode
            errorDescription
          }
        }
      `,
      skip() {
        return !this.deviceId;
      },
      variables() {
        return {
          deviceId: this.deviceId,
        };
      },
      update(data) {
        return data.device_errors;
      },
      fetchPolicy: 'cache-and-network',
    },
    errors: {
      query: gql`
        query GetErrors(
          $limit: Int,
          $offset: Int,
          $order_by: [device_errors_order_by!],
          $errorCodes: [Int!]
          $deviceId: uuid!
        ) {
          device_errors_aggregate (
            where: {
              deviceId: {
                _eq: $deviceId
              }
              errorCode: {
                _in: $errorCodes
              }
            }
          ) {
            aggregate {
              count
            }
          }
          device_errors(
            limit: $limit,
            offset: $offset,
            order_by: $order_by,
            where: {
              deviceId: {
                _eq: $deviceId
              }
              errorCode: {
                _in: $errorCodes
              }
            }
          ) {
            errorCode
            errorDescription
            count
            firstErrorAt
            lastErrorAt
          }
        }
      `,
      variables() {
        const { sortBy, descending, page, rowsPerPage } = this.pagination;
        return {
          limit: rowsPerPage >= 0 ? rowsPerPage : undefined,
          offset: rowsPerPage * (page - 1) || 0,
          order_by: sortBy ? { [sortBy]: descending ? 'desc' : 'asc' } : null,
          errorCodes: this.errorCodeFilter.length > 0 ? this.errorCodeFilter : undefined,
          deviceId: this.deviceId,
        };
      },
      update(data) {
        this.totalCount = data.device_errors_aggregate.aggregate.count;
        return data.device_errors;
      },
      fetchPolicy: 'cache-and-network',
    },
  },

  methods: {
    formatTime(isoString) {
      return DateTime.fromISO(isoString).toLocaleString(DateTime.DATETIME_FULL);
    },
  },
};
</script>

<style>
.filter-box {
  margin: 16px;
}

.filter-input {
  max-width: 16rem;
}
</style>
